import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import ViewColumnRoundedIcon from "@mui/icons-material/ViewColumnRounded";
import { Box, InputAdornment, Stack, TextField } from "@mui/material";

import FilterChip from "./FilterChip";
import ToolbarIconButton from "./ToolbarIconButton";
import { useIntl } from "react-intl";
import ORDER_STATUSES from "./helpers/orderCategories";


const debug_filter = (filters) => {
  
  return filters;
}

const IncomingOrdersToolbar = ({
  search,
  filters,
  activeFilterCount,
  onSearchChange,
  onSearchKeyDown,
  onFilterMenuOpen,
  onColumnPanelOpen,
  onSettingsOpen,
  getTypeVisual,
  getStatusLabel,
  onRemoveType,
  onRemoveSupplierStatus,
  onRemoveOrderStatus,
  onClearDateFilter,
}) => {
  const intl = useIntl();

  const orderStatusLabel =
    ORDER_STATUSES.find(s => s.key === filters?.orderStatus)?.label;

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      alignItems={{ xs: "stretch", sm: "center" }}
      spacing={1}
      sx={{ mt: 0.5, mb: 1.25 }}
    >
      <TextField
        size="small"
        placeholder={intl.formatMessage({ id: "incomingOrders.toolbar.searchPlaceholder" })}
        value={search}
        onChange={(event) => onSearchChange(event.target.value)}
        onKeyDown={onSearchKeyDown}
        sx={{
          width: { xs: "100%", sm: 260, md: 320 },
          "& .MuiOutlinedInput-root": {
            height: 34,
            borderRadius: "8px",
            backgroundColor: "#fff",
            fontSize: 13,
            overflow: "visible",
            "& fieldset": { borderColor: "rgba(0,0,0,0.12)" },
            "&:hover fieldset": { borderColor: "rgba(0,0,0,0.22)" },
            "&.Mui-focused fieldset": { borderColor: "primary.main", borderWidth: 1 },
          },
          "& .MuiInputBase-input": { py: 0, fontSize: 13 },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" sx={{ ml: 0.25 }}>
              <SearchRoundedIcon sx={{ fontSize: 16, color: "text.disabled" }} />
            </InputAdornment>
          ),
        }}
      />

      <Stack direction="row" flexWrap="wrap" spacing={0.75} alignItems="center" sx={{ flex: 1 }}>
        {(debug_filter(filters)?.productTypes ?? []).length === 0 ? (
          <FilterChip label={intl.formatMessage({ id: "incomingOrders.toolbar.allTypes" })} variant="type" />
        ) : (
            filters.productTypes.map((id) => {
            const { label, icon } = getTypeVisual(id);
            return (
              <FilterChip
                key={id}
                label={`${icon} ${label}`}
                variant="type"
                onDelete={() => onRemoveType(id)}
              />
            );
          })
          )}

        {filters?.orderStatus ? (
          <FilterChip label={orderStatusLabel ?? filters.orderStatus} variant="status" onDelete={() => onRemoveOrderStatus()} />
        ) : (
            <FilterChip
              label={intl.formatMessage({ id: "incomingOrders.toolbar.allStatuses" })}
              variant="neutral"              
            />
          )}

        {(filters?.supplierOrderStatusIds ?? []).length === 0 ? (
          <FilterChip label={intl.formatMessage({ id: "incomingOrders.toolbar.allStatuses" })} variant="neutral" />
        ) : (
            filters.supplierOrderStatusIds.map((id) => (
            <FilterChip
              key={id}
              label={intl.formatMessage({ id: "incomingOrders.toolbar.statusFilterLabel" }, { status: getStatusLabel(id) })}
              variant="status"
                onDelete={() => onRemoveSupplierStatus(id)}
            />
          ))
        )}

        {(filters.fromDate || filters.toDate) && (
          <FilterChip
            label={intl.formatMessage(
              { id: "incomingOrders.toolbar.receivedRangeLabel" },
              {
                from: filters.fromDate
                  ? intl.formatDate(filters.fromDate)
                  : intl.formatMessage({ id: "incomingOrders.toolbar.fromStart" }),
                to: filters.toDate
                  ? intl.formatDate(filters.toDate)
                  : intl.formatMessage({ id: "incomingOrders.toolbar.today" }),
              }
            )}
            variant="neutral"
            onDelete={onClearDateFilter}
          />
        )}
      </Stack>

      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
        <ToolbarIconButton title={intl.formatMessage({ id: "incomingOrders.toolbar.filterByTypeAndStatus" })} onClick={onFilterMenuOpen}>
          <Box sx={{ position: "relative", display: "flex", alignItems: "center", overflow: "visible" }}>
            <FilterListRoundedIcon sx={{ fontSize: 18 }} />
            {activeFilterCount > 0 && (
              <Box
                sx={{
                  position: "absolute",
                  top: -5,
                  right: -5,
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  backgroundColor: "primary.main",
                  color: "#fff",
                  fontSize: 9,
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1,
                  opacity: 0.86,
                }}
              >
                {activeFilterCount}
              </Box>
            )}
          </Box>
        </ToolbarIconButton>

        <ToolbarIconButton title={intl.formatMessage({ id: "incomingOrders.toolbar.showHideColumns" })} onClick={onColumnPanelOpen}>
          <ViewColumnRoundedIcon sx={{ fontSize: 18 }} />
        </ToolbarIconButton>

        <ToolbarIconButton title={intl.formatMessage({ id: "incomingOrders.toolbar.settings" })} onClick={onSettingsOpen}>
          <SettingsRoundedIcon sx={{ fontSize: 18 }} />
        </ToolbarIconButton>
      </Stack>
    </Stack>
  );
};

export default IncomingOrdersToolbar;
